export interface SEOData {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogType?: string;
  ogImage?: string;
  schema?: any;
}

export const getSEOData = (page: string, params?: any): SEOData => {
  const baseUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const seoData: Record<string, SEOData> = {
    home: {
      title: 'Previsão do Tempo Brasil - Clima Atualizado e Alertas Meteorológicos',
      description: 'Previsão do tempo precisa para todas as cidades do Brasil. Alertas meteorológicos, temperatura, umidade, vento e previsão para 7 dias. Consulte o clima atualizado agora.',
      keywords: 'previsão do tempo rj, previsão do tempo, clima brasil, temperatura, previsão do tempo rio de janeiro, tempo hoje, previsão do tempo sp,',
      canonical: `${baseUrl}/`,
      ogType: 'website',
      ogImage: 'https://static.readdy.ai/image/1734aa6e699eb773e3c21f1f9a11a08e/c009d2816740129545b723f6308e8421.jpeg',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Previsão do Tempo Brasil',
        url: baseUrl,
        description: 'Previsão do tempo precisa para todas as cidades do Brasil com alertas meteorológicos',
        potentialAction: {
          '@type': 'SearchAction',
          target: `${baseUrl}/cidade/{search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      }
    },
    cities: {
      title: 'Cidades do Brasil - Previsão do Tempo por Cidade',
      description: 'Consulte a previsão do tempo para todas as cidades brasileiras. Temperatura, umidade, vento e alertas meteorológicos atualizados para sua cidade.',
      keywords: 'cidades brasil, previsão tempo cidades, clima cidades brasileiras, tempo por cidade',
      canonical: `${baseUrl}/cidades`,
      ogType: 'website',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: 'Cidades do Brasil',
        description: 'Lista completa de cidades brasileiras com previsão do tempo',
        url: `${baseUrl}/cidades`
      }
    },
    alerts: {
      title: 'Alertas Meteorológicos Brasil - Avisos de Tempo Severo',
      description: 'Alertas meteorológicos em tempo real para todo o Brasil. Avisos de tempestades, chuvas fortes, ventos e outras condições climáticas severas.',
      keywords: 'alertas meteorológicos, avisos tempo severo, tempestades brasil, alertas clima',
      canonical: `${baseUrl}/alertas`,
      ogType: 'website',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Alertas Meteorológicos',
        description: 'Alertas e avisos meteorológicos em tempo real',
        url: `${baseUrl}/alertas`
      }
    },
    blog: {
      title: 'Blog - Dicas e Informações sobre Clima e Tempo',
      description: 'Artigos, dicas e informações sobre previsão do tempo, mudanças climáticas, fenômenos meteorológicos e como se proteger das condições climáticas.',
      keywords: 'blog clima, artigos tempo, dicas meteorologia, informações clima',
      canonical: `${baseUrl}/blog`,
      ogType: 'website',
      schema: {
        '@context': 'https://schema.org',
        '@type': 'Blog',
        name: 'Blog Previsão do Tempo',
        description: 'Artigos e dicas sobre clima e meteorologia',
        url: `${baseUrl}/blog`
      }
    },
    about: {
      title: 'Sobre Nós - Previsão do Tempo Brasil',
      description: 'Conheça nossa missão de fornecer previsões meteorológicas precisas e acessíveis para todos os brasileiros.',
      keywords: 'sobre previsão tempo, quem somos, missão clima brasil',
      canonical: `${baseUrl}/sobre`,
      ogType: 'website'
    },
    contact: {
      title: 'Contato - Fale Conosco',
      description: 'Entre em contato conosco para dúvidas, sugestões ou parcerias relacionadas à previsão do tempo.',
      keywords: 'contato previsão tempo, fale conosco, suporte clima',
      canonical: `${baseUrl}/contato`,
      ogType: 'website'
    },
    privacy: {
      title: 'Política de Privacidade - Previsão do Tempo Brasil',
      description: 'Política de privacidade e proteção de dados do site Previsão do Tempo Brasil.',
      keywords: 'política privacidade, proteção dados, lgpd',
      canonical: `${baseUrl}/privacy`,
      ogType: 'website'
    },
    terms: {
      title: 'Termos de Uso - Previsão do Tempo Brasil',
      description: 'Termos e condições de uso do site Previsão do Tempo Brasil.',
      keywords: 'termos uso, condições uso, regras site',
      canonical: `${baseUrl}/terms`,
      ogType: 'website'
    }
  };

  return seoData[page] || seoData.home;
};

export const getCitySEO = (cityName: string, stateName?: string, countryName?: string) => {
  const location = stateName 
    ? `${cityName}, ${stateName}, ${countryName || 'Brasil'}`
    : `${cityName}, ${countryName || 'Brasil'}`;
  
  const title = `Previsão do Tempo em ${location} - Clima Atualizado`;
  const description = `Confira a previsão do tempo completa para ${location}. Temperatura atual, previsão para os próximos dias, alertas meteorológicos e muito mais.`;
  
  return {
    title,
    description,
    keywords: `previsão do tempo ${cityName}, clima ${cityName}, temperatura ${cityName}, tempo ${location}, meteorologia ${cityName}`,
    canonical: `/cidade/${cityName.toLowerCase().replace(/\s+/g, '-')}`,
  };
};

export const getStateSEO = (stateName: string, stateCode: string): SEOData => {
  const baseUrl = import.meta.env.VITE_SITE_URL || 'https://previsaodotempobrasil.com.br';
  
  return {
    title: `Previsão do Tempo em ${stateName} - Todas as Cidades`,
    description: `Consulte a previsão do tempo para todas as cidades de ${stateName}. Temperatura, umidade, vento e alertas meteorológicos atualizados em tempo real.`,
    keywords: `previsão do tempo ${stateName}, clima ${stateName}, tempo ${stateCode}, cidades ${stateName}, meteorologia ${stateName}`,
    canonical: `${baseUrl}/estado/${stateCode.toLowerCase()}`,
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: `Previsão do Tempo em ${stateName}`,
      description: `Previsão do tempo completa para todas as cidades de ${stateName}`,
      url: `${baseUrl}/estado/${stateCode.toLowerCase()}`
    }
  };
};

export const getArticleSEO = (title: string, excerpt: string, slug: string, author: string, date: string): SEOData => {
  const baseUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    title: `${title} - Blog Previsão do Tempo`,
    description: excerpt,
    keywords: `${title.toLowerCase()}, clima, meteorologia, tempo, previsão`,
    canonical: `${baseUrl}/blog/${slug}`,
    ogType: 'article',
    ogImage: 'https://static.readdy.ai/image/1734aa6e699eb773e3c21f1f9a11a08e/c009d2816740129545b723f6308e8421.jpeg',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: title,
      description: excerpt,
      author: {
        '@type': 'Person',
        name: author
      },
      datePublished: date,
      url: `${baseUrl}/blog/${slug}`,
      publisher: {
        '@type': 'Organization',
        name: 'Previsão do Tempo Brasil',
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/1734aa6e699eb773e3c21f1f9a11a08e/c009d2816740129545b723f6308e8421.jpeg'
        }
      }
    }
  };
};
